import { VOLUME_NAME_RE } from "../volumes";
import {
  isValidStepTestName,
  MAX_STEP_TEST_BYTES,
  STEP_TEST_CASES_DIR,
} from "./constants";

export const STEP_TEST_VOLUME_FIXTURE_DIR = "volumes";
export const STEP_TEST_VOLUME_BASE64_SENTINEL =
  "__3B_STEP_TEST_VOLUME_BASE64__";
export const STEP_TEST_SQLITE_DUMP_SENTINEL = "__3B_STEP_TEST_SQLITE_DUMP__";
export const STEP_TEST_SERIALIZED_SUFFIX = ".serialized";
export const MAX_STEP_TEST_VOLUME_FILES = 200;
export const MAX_STEP_TEST_VOLUME_TOTAL_BYTES = MAX_STEP_TEST_BYTES;

export type StepTestVolumeFixtureFile = {
  volume: string;
  path: string;
  content: string;
  encoding?: "utf8" | "base64" | "sqlite-dump" | "serialized";
};

// Internal model parsed from the wire shape above: a serialized whole-volume
// snapshot is its own kind rather than a file with an empty-path sentinel.
export type StepTestVolumeFixture =
  | {
      kind: "file";
      volume: string;
      path: string;
      content: string;
      encoding: "utf8" | "base64" | "sqlite-dump";
    }
  | { kind: "serialized"; volume: string; content: string };

export function toStepTestVolumeFixture(
  file: StepTestVolumeFixtureFile
): StepTestVolumeFixture {
  const encoding = stepTestVolumeFixtureEncoding(file);
  if (encoding === "serialized") {
    return { kind: "serialized", volume: file.volume, content: file.content };
  }
  return {
    kind: "file",
    volume: file.volume,
    path: file.path,
    content: file.content,
    encoding,
  };
}

export function volumeFixtureBaseRelPath(name: string) {
  return `${STEP_TEST_CASES_DIR}/${name}.${STEP_TEST_VOLUME_FIXTURE_DIR}`;
}

export function volumeFixturePhaseRelPath(
  name: string,
  phase: "initial" | "expected"
) {
  return `${volumeFixtureBaseRelPath(name)}/${phase}`;
}

export function volumeFixtureRelPath(
  name: string,
  phase: "initial" | "expected",
  fixture: StepTestVolumeFixture
) {
  if (fixture.kind === "serialized") {
    return `${volumeFixturePhaseRelPath(name, phase)}/${fixture.volume}${STEP_TEST_SERIALIZED_SUFFIX}`;
  }
  return `${volumeFixturePhaseRelPath(name, phase)}/${fixture.volume}/${fixture.path}`;
}

export function isStepTestVolumeRelPath(relPath: string): boolean {
  return (
    relPath.startsWith(`${STEP_TEST_CASES_DIR}/`) &&
    relPath.includes(`.${STEP_TEST_VOLUME_FIXTURE_DIR}/`)
  );
}

export function isStepTestVolumeRelPathForName(
  relPath: string,
  name: string
): boolean {
  return relPath.startsWith(`${volumeFixtureBaseRelPath(name)}/`);
}

export function volumeFixtureNameFromRelPath(relPath: string): string | null {
  if (!relPath.startsWith(`${STEP_TEST_CASES_DIR}/`)) return null;
  const rest = relPath.slice(STEP_TEST_CASES_DIR.length + 1);
  const marker = `.${STEP_TEST_VOLUME_FIXTURE_DIR}/`;
  const markerIndex = rest.indexOf(marker);
  if (markerIndex === -1) return null;
  const name = rest.slice(0, markerIndex);
  return isValidStepTestName(name) ? name : null;
}

export function validateStepTestVolumeFixtureFile(
  file: StepTestVolumeFixtureFile
): string | null {
  if (!VOLUME_NAME_RE.test(file.volume)) return "Invalid volume name";
  if (
    file.encoding &&
    file.encoding !== "utf8" &&
    file.encoding !== "base64" &&
    file.encoding !== "sqlite-dump" &&
    file.encoding !== "serialized"
  ) {
    return "Invalid volume fixture encoding";
  }
  const encoding = stepTestVolumeFixtureEncoding(file);
  if (encoding === "serialized") {
    if (file.path !== "") return "Invalid volume fixture path";
  } else {
    if (!file.path || file.path.startsWith("/") || file.path.endsWith("/")) {
      return "Invalid volume fixture path";
    }
    const parts = file.path.split("/");
    if (parts.some((part) => part === "" || part === "." || part === "..")) {
      return "Invalid volume fixture path";
    }
    if (/\s/.test(file.path)) return "Invalid volume fixture path";
    if (
      encoding === "utf8" &&
      (file.content.startsWith(`${STEP_TEST_VOLUME_BASE64_SENTINEL}\n`) ||
        file.content.startsWith(`${STEP_TEST_SQLITE_DUMP_SENTINEL}\n`))
    ) {
      return "Volume fixture content uses a reserved marker";
    }
  }
  const byteLength = stepTestVolumeFixtureDecodedLength(file);
  if (byteLength === null) return "Invalid base64 volume fixture content";
  if (byteLength > MAX_STEP_TEST_BYTES) {
    return "Step test volume fixture files must each be under 256 KB";
  }
  return null;
}

export function stepTestVolumeFixtureEncoding(
  file: StepTestVolumeFixtureFile
): "utf8" | "base64" | "sqlite-dump" | "serialized" {
  return file.encoding ?? "utf8";
}

export function serializeStepTestVolumeFixtureContent(
  fixture: StepTestVolumeFixture
): string {
  // Serialized snapshots are stored verbatim: they are only ever compared,
  // never re-materialized, so they need no decode marker.
  if (fixture.kind === "serialized") return fixture.content;
  if (fixture.encoding === "base64") {
    return `${STEP_TEST_VOLUME_BASE64_SENTINEL}\n${fixture.content}`;
  }
  if (fixture.encoding === "sqlite-dump") {
    return `${STEP_TEST_SQLITE_DUMP_SENTINEL}\n${fixture.content}`;
  }
  return fixture.content;
}

export function parseStepTestVolumeFixtureContent(
  content: string
): Pick<StepTestVolumeFixtureFile, "content" | "encoding"> {
  if (content.startsWith(`${STEP_TEST_VOLUME_BASE64_SENTINEL}\n`)) {
    return {
      content: content.slice(STEP_TEST_VOLUME_BASE64_SENTINEL.length + 1),
      encoding: "base64",
    };
  }
  if (content.startsWith(`${STEP_TEST_SQLITE_DUMP_SENTINEL}\n`)) {
    return {
      content: content.slice(STEP_TEST_SQLITE_DUMP_SENTINEL.length + 1),
      encoding: "sqlite-dump",
    };
  }
  return { content, encoding: "utf8" };
}

export function stepTestVolumeFixtureDecodedLength(
  file: StepTestVolumeFixtureFile
): number | null {
  if (stepTestVolumeFixtureEncoding(file) !== "base64") {
    return Buffer.byteLength(file.content, "utf-8");
  }
  try {
    const buffer = Buffer.from(file.content, "base64");
    if (buffer.toString("base64") !== file.content.replace(/\s/g, "")) {
      return null;
    }
    return buffer.byteLength;
  } catch {
    return null;
  }
}

export function validateStepTestVolumeFixtureFiles(
  files: readonly StepTestVolumeFixtureFile[]
): string | null {
  if (files.length > MAX_STEP_TEST_VOLUME_FILES) {
    return `Step tests may include at most ${MAX_STEP_TEST_VOLUME_FILES} volume fixture files`;
  }
  let total = 0;
  const seen = new Set<string>();
  for (const file of files) {
    const error = validateStepTestVolumeFixtureFile(file);
    if (error) return error;
    const key = `${file.volume}/${file.path}`;
    if (seen.has(key)) return `Duplicate volume fixture path: ${key}`;
    seen.add(key);
    total += stepTestVolumeFixtureDecodedLength(file) ?? 0;
  }
  if (total > MAX_STEP_TEST_VOLUME_TOTAL_BYTES) {
    return "Step test volume fixtures must total under 256 KB";
  }
  return null;
}

export function bytesAreUtf8Text(bytes: Buffer): boolean {
  return Buffer.from(bytes.toString("utf-8"), "utf-8").equals(bytes);
}

export function seededSerializedError(
  files: readonly StepTestVolumeFixtureFile[]
): string | null {
  return files.some((f) => f.encoding === "serialized")
    ? "Serialized snapshots can only be captured, not seeded"
    : null;
}

export function opaqueCapturedVolumeFiles(
  files: readonly StepTestVolumeFixtureFile[]
): StepTestVolumeFixtureFile[] {
  return files.filter((file) => {
    if (stepTestVolumeFixtureEncoding(file) !== "base64") return false;
    return !bytesAreUtf8Text(Buffer.from(file.content, "base64"));
  });
}
